import * as support from "./AggregatedClaimsTable.support";
import {
  DeleteRounded as DeleteIcon,
  RestoreRounded as RestoreIcon,
} from "@mui/icons-material";
import {
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import { currencyFormat } from "common/numbers";
import Component from "components/Component";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import * as labels from "config.labels";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useClaimsAggregateQuery } from "services/claimsService";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import * as featureFlagHooks from "store/hooks/featureFlagHooks";
import { useTranslator } from "store/hooks/staticDataHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const BlankSlate = ({ message }) => (
  <Stack alignItems={"center"} justifyContent={"center"}>
    <Typography
      sx={{
        padding: "5rem",
        color: "gray !important",
      }}
    >
      {message}
    </Typography>
  </Stack>
);

const Loader = () => (
  <Stack alignItems={"center"} justifyContent={"center"} padding={"65px"}>
    <DefaultLoader size={48} />
  </Stack>
);

const Headings = ({ interactive }) => (
  <TableHead>
    <TableRow>
      <TableCell align={"center"}>{"Period"}</TableCell>
      <TableCell align={"center"}>{"№ of Claims"}</TableCell>
      <TableCell align={"right"}>{"Paid"}</TableCell>
      <TableCell align={"right"}>{"Reserved"}</TableCell>
      <TableCell align={"right"}>{"Incurred"}</TableCell>
      {interactive && <TableCell />}
    </TableRow>
  </TableHead>
);

const NumberCell = ({ value }) => (
  <TableCell align={"right"} sx={{ minWidth: "10rem" }}>
    <span className={"tabular"}>{currencyFormat(value)}</span>
  </TableCell>
);

const Row = ({ inception, data, interactive, isHidden, onHide, onRestore }) => {
  const { isOn: bucketByPolicyYear } = featureFlagHooks.useFeatureFlag(
    "claims/bucketAggregatedClaimsByPolicyYear",
    {
      default: true,
    }
  );
  return (
    <TableRow sx={isHidden ? { opacity: 0.5 } : undefined}>
      <TableCell align={"center"}>
        {bucketByPolicyYear
          ? support.formatDate(inception, data.year)
          : data.year}
      </TableCell>
      <TableCell align={"center"}>{data.count}</TableCell>
      <NumberCell value={data.paid} />
      <NumberCell value={data.reserved} />
      <NumberCell value={data.incurred} />
      {interactive && (
        <TableCell align={"right"} padding={"none"}>
          {isHidden ? (
            <IconButton
              icon={RestoreIcon}
              scale={"small"}
              tooltip={"Restore year"}
              onClick={onRestore}
            />
          ) : (
            <IconButton
              icon={DeleteIcon}
              scale={"small"}
              tooltip={"Delete year"}
              onClick={onHide}
            />
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

const AggregateTable = ({
  inception,
  aggregate,
  labelVariant,
  interactive,
  hiddenYears,
  onHideYear,
  onRestoreYear,
}) => {
  const [showHidden, setShowHidden] = useState(false);
  const hiddenForCoverage = support.getHiddenYears(
    hiddenYears,
    aggregate.coverage
  );
  const dataSorted = aggregate.data.toSorted((a, b) => b.year - a.year);
  const visibleRows = dataSorted.filter(
    (row) => !hiddenForCoverage.includes(row.year)
  );
  const hiddenRows = dataSorted.filter((row) =>
    hiddenForCoverage.includes(row.year)
  );

  return (
    <Stack>
      <Typography variant={labelVariant ?? "subtitle2"}>
        {aggregate.label}
      </Typography>
      <TableContainer className={"fade-in-slow"}>
        <Table size={"small"} className={"print-table-tiny"}>
          <Headings interactive={interactive} />
          <TableBody>
            {visibleRows.map((row) => (
              <Row
                key={row.year}
                inception={inception}
                data={row}
                interactive={interactive}
                onHide={() => onHideYear(aggregate.coverage, row.year)}
              />
            ))}
            {interactive &&
              showHidden &&
              hiddenRows.map((row) => (
                <Row
                  key={row.year}
                  inception={inception}
                  data={row}
                  interactive={interactive}
                  isHidden
                  onRestore={() => onRestoreYear(aggregate.coverage, row.year)}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {interactive && hiddenRows.length > 0 && (
        <Link
          component={"button"}
          variant={"caption"}
          underline={"hover"}
          onClick={() => setShowHidden((prev) => !prev)}
          sx={{ alignSelf: "flex-start", marginTop: 0.5 }}
        >
          {showHidden
            ? "Hide deleted years"
            : `Show ${hiddenRows.length} deleted year${
                hiddenRows.length === 1 ? "" : "s"
              }`}
        </Link>
      )}
    </Stack>
  );
};

const Content = ({
  data,
  inception,
  loading,
  blankSlateMessage,
  labelVariant,
  interactive,
  hiddenYears,
  onHideYear,
  onRestoreYear,
}) => {
  const showBlankSlate = !loading && (data ?? []).length === 0;

  if (loading) {
    return <Loader />;
  } else if (showBlankSlate) {
    return <BlankSlate message={blankSlateMessage} />;
  }

  return (
    <Stack gap={3}>
      {data.map((aggregate, index) => (
        <AggregateTable
          key={index}
          inception={inception}
          aggregate={aggregate}
          labelVariant={labelVariant}
          interactive={interactive}
          hiddenYears={hiddenYears}
          onHideYear={onHideYear}
          onRestoreYear={onRestoreYear}
        />
      ))}
    </Stack>
  );
};

const AggregatedClaimsTable = ({ variant }) => {
  const dispatch = useDispatch();
  const translateLabel = useTranslator("uiLabels");
  const { isOn: bucketByPolicyYear } = featureFlagHooks.useFeatureFlag(
    "claims/bucketAggregatedClaimsByPolicyYear",
    {
      default: true,
    }
  );
  const claimsQuery = useDebouncedValue(
    useSelector(pricingSelectors.selectPricingClaims)
  );
  const inception = useSelector(programSelectors.getInception);
  const start = bucketByPolicyYear
    ? dates.adjustDate(inception, { years: -10 })
    : null;
  const hasClaimsQueryKey = claimsQuery?.keys?.length > 0;
  const lossCategories =
    useSelector(exposureSelectors.selectLossCategories) ?? [];
  const aggregatedClaims = useSelector(claimsSelectors.selectAggregatedClaims);
  const usingAggregatedClaimsInput = aggregatedClaims.manualMode ?? false;
  const manualData = aggregatedClaims.data ?? [];
  const hiddenYears = usingAggregatedClaimsInput
    ? {}
    : aggregatedClaims.hiddenYears ?? {};
  const { data, isLoading, isFetching } = useClaimsAggregateQuery(
    {
      claimsQuery: {
        ...claimsQuery,
        start,
      },
    },
    { skip: usingAggregatedClaimsInput || !hasClaimsQueryKey }
  );

  const dataToDisplay = usingAggregatedClaimsInput
    ? manualData.map((x) => ({
        coverage: x.coverage,
        label: support.coverageLabel(lossCategories, x.coverage),
        data: x.data,
      }))
    : hasClaimsQueryKey
    ? support.fillMissingYears(data, lossCategories, {
        endDate: inception,
        numberOfYears: 10,
      })
    : [];

  const interactive = variant !== "inline" && !usingAggregatedClaimsInput;

  const handleHideYear = (coverage, year) => {
    dispatch(
      claimsActions.updateAggregatedClaims({
        hiddenYears: support.hideYear(hiddenYears, coverage, year),
      })
    );
  };

  const handleRestoreYear = (coverage, year) => {
    dispatch(
      claimsActions.updateAggregatedClaims({
        hiddenYears: support.restoreYear(hiddenYears, coverage, year),
      })
    );
  };

  const blankSlateMessage =
    dataToDisplay.length === 0 && Object.entries(data ?? {}).length > 0
      ? "No claims falling within the past ten years"
      : "No claims loaded at present";

  if (variant === "inline") {
    return (
      <Content
        inception={inception}
        data={dataToDisplay}
        loading={isLoading}
        blankSlateMessage={blankSlateMessage}
        labelVariant={"caption"}
        interactive={interactive}
        hiddenYears={hiddenYears}
        onHideYear={handleHideYear}
        onRestoreYear={handleRestoreYear}
      />
    );
  }

  return (
    <Component
      title={translateLabel(labels.AGGREGATED_CLAIMS_TABLE_TITLE, {
        default: "Aggregated Claims",
      })}
      isFetching={isFetching}
    >
      <Content
        inception={inception}
        data={dataToDisplay}
        loading={isLoading}
        blankSlateMessage={blankSlateMessage}
        interactive={interactive}
        hiddenYears={hiddenYears}
        onHideYear={handleHideYear}
        onRestoreYear={handleRestoreYear}
      />
    </Component>
  );
};

export default AggregatedClaimsTable;
